import { z } from 'zod';
import { UserRegionSchema } from './onboarding';

/**
 * Контракт `GET /api/me` — Block 0 Phase 4 (ADR-0014 §«Решение» Q1-Q2-Q6).
 *
 * Phase 2 ADR-0012 §3 «Boundary: identity ≠ subscription state» намеренно
 * оставила subscription state ВНЕ `RegisterResponseSchema` (только identity:
 * jwt + userId + region). Этот файл закрывает обещанный resource: subscription
 * state читается отдельным fetch'ем через `apiClient.me.get()`. Симметрия с
 * `data-model.md` §10 (users) vs §11 (subscriptions) — два domain object,
 * связанные 1:1 через `users.id` PK.
 *
 * ── Канон состояний (5 значений из data-model.md §11.1 line 721-727) ─────
 *   - `trial`            — 15 дней с момента первой регистрации.
 *   - `grace`            — 7 дней после триала (баннер «Подписка истекла»).
 *   - `paid`             — активная подписка.
 *   - `expired_readonly` — 30 дней read-only после grace.
 *   - `archived`         — данные хранятся 12 месяцев → удаление по запросу.
 *
 * **Внимание:** PRD §2.3 line 99-100 использовал упрощённое 4-state matrix
 * (`expired` без `_readonly`). Берём канон data-model.md (forward-compatible
 * с Phase 9+ Drizzle enum). См. ADR-0014 §«Контекст» constraint 3.
 *
 * ── Минимальный shape Phase 4 ───────────────────────────────────────────
 * Phase 4 нуждается в `userId`, `region`, `subscriptionStatus`,
 * `trialStartedAt`, `expiresAt`. Поля `currentPlanCode` / `currentProvider`
 * (data-model.md §11.1 line 746-747) НЕ включены: они нужны Block 11
 * (Экран 2.11.3 «Подписка / оплата») для UI «Оплатить через Kaspi». Phase 4
 * расширит их optional если Block 11 появится раньше Phase 9+ backend.
 *
 * ── `.strict()` ──────────────────────────────────────────────────────────
 * Защита от data leak / injection (см. `docs/examples/zod-contract.ts`
 * § «Response schema»). Если Phase 9+ backend начнёт возвращать дополнительные
 * поля (например, `lastSeenAt`), фронт **отвергнет** response через
 * `apiClient.me.get()` — это нужный сигнал для контракт-amendment ADR.
 */

// ── Subscription state enum ─────────────────────────────────────────────
// Канон из data-model.md §11.1 line 721-727. Не PRD §2.3 (упрощённый).
export const SUBSCRIPTION_STATUSES = [
  'trial',
  'grace',
  'paid',
  'expired_readonly',
  'archived',
] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

export const SubscriptionStatusSchema = z.enum(SUBSCRIPTION_STATUSES);

// ── ISO 8601 timestamp ──────────────────────────────────────────────────
// `z.string().datetime()` принимает RFC 3339 / ISO 8601 с `Z` суффиксом или
// явным offset (`+05:00`). Phase 9+ backend Postgres `timestamp with timezone`
// выдаёт `2026-05-08T11:23:45.123Z` (UTC) или `2026-05-08T16:23:45+05:00`.
// MSW Phase 4 использует `new Date(...).toISOString()` — всегда UTC `Z`.
const Iso8601Schema = z.string().datetime({ offset: true });

// ── MeResponseSchema ────────────────────────────────────────────────────
// `userId` UUID v4 (P-010 — UUID, не messenger external_id). Региональный
// Postgres `users.id` имеет `defaultRandom()` → `gen_random_uuid()` v4.
// `region` — re-export `UserRegionSchema` из onboarding (collision-safe alias
// для image-region из decor-match.ts).
export const MeResponseSchema = z
  .object({
    userId: z.string().uuid(),
    region: UserRegionSchema,
    subscriptionStatus: SubscriptionStatusSchema,
    trialStartedAt: Iso8601Schema,
    expiresAt: Iso8601Schema,
  })
  .strict();

export type MeResponse = z.infer<typeof MeResponseSchema>;
